/*
 * Automatic messages: what each one is, its steps, and the words it starts with (copied from the WordPress Steward, which
 * copied Jobber's). Every automation is off for a new company. The server's runner (apps/api/src/automations) decides when
 * each step is due and puts the message in the outbox, where quiet hours, opt-outs and the live switch still apply.
 *
 * Owner rules kept here so the server, the web screens and the tests share them:
 * - Quote follow-up texts end "View your quote here: {link}", invoice and debt texts end "Pay your invoice here:" with the link on its own line (the old Steward's).
 *   The ending is added by the app and cannot be edited away.
 * - Never "This is {business}" to a current client. Only the booking confirmation (sent to someone who just asked for a
 *   price) may say it.
 */
import { z } from "zod";

export const AUTOMATION_KINDS = [
  "booking_confirm",
  "job_booked",
  "visit_reminder",
  "quote_followup",
  "invoice_reminder",
  "debt_reminder",
  "receipt",
  "visit_report",
  "review_request",
  "referral_offer",
  "plan_care",
  "due_again",
  "win_back",
  "past_clients",
] as const;
export type AutomationKind = (typeof AUTOMATION_KINDS)[number];

export const STEP_CHANNELS = ["sms", "email"] as const;

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

export const automationStep = z.object({
  /** Days from the anchor (the visit day for reminders, so -1 is the day before; the send day for the rest). */
  day: z.number().int().min(-30).max(400),
  /** The time of day in the company's time zone, or null for the same time as the anchor. */
  time: hhmm.nullable(),
  channel: z.enum(STEP_CHANNELS),
  text: z.string().min(1).max(1000),
  subject: z.string().max(160),
  /** The email body. Empty means the text is used. */
  email: z.string().max(4000),
});
export type AutomationStep = z.infer<typeof automationStep>;

export interface AutomationInfo {
  title: string;
  /** One plain line for the list row. */
  what: string;
  /** transactional texts go 8 AM to 8 PM; marketing 9 AM to 7 PM and never to people who said no. */
  purpose: "transactional" | "marketing";
  /** What day 0 is. */
  anchor: string;
  /** Added to every text, never editable. */
  ending: string | null;
  /** May the text say "this is {business}"? Only for people who are not clients yet. */
  mayIntroduce: boolean;
  /** On for a new company (receipts, owner, question 72). Everything else starts off. */
  defaultOn?: boolean;
  /** Days between repeats after the last step (debt reminders), or null. */
  repeatDays: number | null;
  /** The words that fill in, for the edit page. */
  fills: readonly string[];
  steps: readonly AutomationStep[];
}

const step = (day: number, time: string | null, channel: "sms" | "email", text: string, subject = "", email = "") => ({
  day,
  time,
  channel,
  text,
  subject,
  email,
});

const INVOICE_MAIL =
  "Hi {name},\n\nThis is a reminder that payment on Invoice {number} for your {job} is {overdue}.\n\nIf the payment has already been sent, please ignore this email.\n\nThanks,\n{business}";

export const AUTOMATIONS: Record<AutomationKind, AutomationInfo> = {
  booking_confirm: {
    title: "Request received",
    what: "Texts a new person right after they ask for a price on the website",
    purpose: "transactional",
    anchor: "the request",
    ending: null,
    mayIntroduce: true,
    repeatDays: null,
    fills: ["{first}", "{business}", "{service}", "{price}"],
    steps: [
      step(
        0,
        null,
        "sms",
        "Hi {first}, this is {business}. We got your {service} request and we're working on your quote now. If you can, reply with a couple photos and we'll get you an exact price faster.",
        "{first}, your quote from {business} is in progress",
        "Hi {first},\n\nThanks for reaching out. We got your {service} request and we're putting your price together now. We'll text you the quote, usually the same day.\n\nWant your price faster? Reply to this email with a few photos.\n\n{business}",
      ),
    ],
  },
  job_booked: {
    title: "Job scheduled",
    what: "Tells a client their job is on the schedule, once, when it is booked",
    purpose: "transactional",
    anchor: "the booking",
    ending: null,
    mayIntroduce: false,
    repeatDays: null,
    fills: ["{first}", "{job}", "{soon}", "{details}", "{business}"],
    steps: [
      step(
        0,
        null,
        "sms",
        "Good news {first}, your {job} is scheduled {soon}!\n\n{details}",
        "{job} confirmation from {business}",
        "Hi {first},\n\nThank you for scheduling your {job} with us.\n\n{details}\n\nSincerely,\n\n{business}",
      ),
    ],
  },
  visit_reminder: {
    title: "Visit reminder",
    what: "Reminds clients of an upcoming visit",
    purpose: "transactional",
    anchor: "the visit day",
    ending: null,
    mayIntroduce: false,
    repeatDays: null,
    fills: ["{first}", "{job}", "{soon}", "{details}", "{business}"],
    steps: [
      // Plain and warm (owner, 2026-11-01): "just a friendly reminder you have a (job) scheduled next week".
      step(-1, "10:00", "sms", "Hi {first}, just a friendly reminder that your {job} is scheduled {soon}.\n\n{details}"),
      step(
        -7,
        "10:00",
        "email",
        "Hi {first}, just a friendly reminder that your {job} is scheduled {soon}.\n\n{details}",
        "Your {job} is scheduled {soon}",
        "Hi {first},\n\nJust a friendly reminder that your {job} is scheduled {soon}.\n\n{details}\n\nSee you then!",
      ),
    ],
  },
  quote_followup: {
    title: "Quote follow-up",
    what: "Follows up on a sent quote until it is approved or turned down",
    purpose: "transactional",
    anchor: "the day the quote was sent",
    ending: "View your quote here: {link}",
    mayIntroduce: false,
    repeatDays: null,
    fills: ["{first}", "{job}", "{number}", "{business}", "{phone}"],
    // Owner, 2026-11-04 (sales research): one short question each, no "filling up fast", no "expires tomorrow" (not every
    // quote has that date). Same words as live on the New Orleans brands.
    steps: [
      step(
        1,
        "10:00",
        "sms",
        "Hi {first}, any questions on your {job} quote? Happy to go over what it covers.",
        "Your {job} quote",
      ),
      step(
        3,
        "10:00",
        "sms",
        "Hi {first}, is it the price or the timing holding up your {job} quote? Either is totally fine.",
        "Your {job} quote",
      ),
      step(
        7,
        "10:00",
        "sms",
        "Hi {first}, did you end up going another way? Totally fine if so, just reply and we'll close out your {job} quote.",
        "Your {job} quote",
      ),
      step(
        20,
        "10:00",
        "sms",
        "Hi {first}, still thinking about your {job} quote? It's right here whenever you're ready.",
        "Your {job} quote",
      ),
      step(
        29,
        "10:00",
        "sms",
        "Hi {first}, should we keep your {job} quote open, or are you all set? Any answer is fine.",
        "Your {job} quote",
      ),
    ],
  },
  invoice_reminder: {
    title: "Invoice reminder",
    what: "Reminds clients to pay a sent invoice. Business clients get theirs after their 7 days",
    purpose: "transactional",
    anchor: "the day the invoice was sent (for a business, the day it is due)",
    ending: "Pay your invoice here:\n{link}",
    mayIntroduce: false,
    repeatDays: null,
    fills: ["{first}", "{name}", "{job}", "{number}", "{total}", "{overdue}", "{business}"],
    steps: [
      step(
        1,
        "10:00",
        "sms",
        "Hi {first}, reminder to pay your {job} invoice. Please pay below.",
        "Your {job} invoice from {business} is {overdue}",
        INVOICE_MAIL,
      ),
      step(
        1,
        "14:30",
        "sms",
        "Hi {first}, your {job} invoice is past due. Pay below.",
        "Your {job} invoice from {business} is {overdue}",
        INVOICE_MAIL,
      ),
      step(
        4,
        "10:00",
        "sms",
        "Hi {first}, your {job} invoice is 4 days past due",
        "Your {job} invoice from {business} is {overdue}",
        INVOICE_MAIL,
      ),
    ],
  },
  debt_reminder: {
    title: "Debt reminder",
    what: "One text for an invoice moved to debt, then one every 30 days, 9 AM to 7 PM",
    purpose: "transactional",
    anchor: "the day it went to debt",
    ending: "Pay your invoice here:\n{link}",
    mayIntroduce: false,
    repeatDays: 30,
    fills: ["{first}", "{job}", "{date}", "{total}"],
    steps: [step(0, null, "sms", "Hi {first}, a reminder that your {job} invoice from {date} for {total} is still open.")],
  },
  receipt: {
    title: "Receipt",
    what: "Emails a receipt when a client pays",
    purpose: "transactional",
    defaultOn: true,
    anchor: "the payment",
    ending: null,
    mayIntroduce: false,
    repeatDays: null,
    fills: ["{first}", "{amount}", "{number}", "{business}"],
    steps: [
      step(
        0,
        null,
        "email",
        "Hi {first}, thank you. We got your payment of {amount} for invoice {number}.",
        "Your receipt from {business}",
      ),
    ],
  },
  visit_report: {
    title: "Visit report",
    what: "Texts a link to a report of what was done, right after a visit is finished",
    purpose: "transactional",
    anchor: "the finished visit",
    ending: "See your visit report here: {link}",
    mayIntroduce: false,
    repeatDays: null,
    fills: ["{first}", "{job}", "{crew}"],
    steps: [step(0, null, "sms", "Hi {first}, your {job} is all done!")],
  },
  review_request: {
    title: "Review request",
    what: "Asks each client once for a rating 2 hours after a finished visit, with up to 2 reminders. 4 and 5 stars go on to Google",
    purpose: "marketing",
    anchor: "2 hours after the visit is done",
    ending: "{review}",
    mayIntroduce: false,
    repeatDays: null,
    fills: ["{first}", "{job}", "{crew}"],
    // Like the old Steward (3.45.0, the owner's handbook): right after the visit, two days later, three days after that.
    // The reminders stop once the client rates.
    steps: [
      step(
        0,
        null,
        "sms",
        "Hi {first}! Hope you love your {job}. {crew} would really appreciate a quick rating, it takes 20 seconds",
      ),
      step(2, null, "sms", "Hi {first}, quick reminder. A 20 second rating helps our local team a lot"),
      step(5, null, "sms", "Last one, promise! {first}, we would love to hear how your {job} went"),
    ],
  },
  referral_offer: {
    title: "Referral offer",
    what: "Sends a client their own link to share after they leave a review",
    purpose: "marketing",
    anchor: "the review request",
    ending: "{referral}",
    mayIntroduce: false,
    repeatDays: null,
    fills: ["{first}", "{business}", "{amount}"],
    steps: [
      step(
        3,
        "10:00",
        "sms",
        "Hi {first}, if you know someone who could use {business}, send them your link. They get {amount} off their first visit, and you get {amount} off your next one after their first visit.",
      ),
    ],
  },
  plan_care: {
    title: "Plan check-in",
    what: "Checks in with clients on a repeating plan after their first few visits",
    purpose: "marketing",
    anchor: "the plan's third finished visit",
    ending: null,
    mayIntroduce: false,
    repeatDays: null,
    fills: ["{first}", "{job}"],
    steps: [
      step(
        1,
        "11:00",
        "sms",
        "Hi {first}, how is your {job} going so far? Anything you want us to do differently? Just reply here.",
      ),
    ],
  },
  due_again: {
    title: "Due again",
    what: "Nudges one-time clients when their next visit would be due",
    purpose: "marketing",
    anchor: "their last finished visit",
    ending: null,
    mayIntroduce: false,
    repeatDays: null,
    fills: ["{first}", "{job}"],
    steps: [
      step(
        30,
        "10:00",
        "sms",
        "Hi {first}, it has been about a month since your {job}. Want us to come back? Just reply with a day that works.",
      ),
    ],
  },
  win_back: {
    title: "Win back",
    what: "Texts clients who stopped, to get them back on the schedule",
    purpose: "marketing",
    anchor: "the day they stopped",
    ending: null,
    mayIntroduce: false,
    repeatDays: null,
    fills: ["{first}"],
    steps: [
      step(
        45,
        "10:00",
        "sms",
        "Hi {first}, it has been a while since we were out. Want us to get you back on the schedule? Just reply here.",
      ),
    ],
  },
  past_clients: {
    title: "Past client calendar",
    what: "One text a month to past one-time clients, with that month's reason, for 12 months",
    purpose: "marketing",
    anchor: "their last cleaning, 60 days or more ago",
    ending: null,
    mayIntroduce: false,
    repeatDays: 30,
    fills: ["{first}", "{month}"],
    steps: [step(60, "10:00", "sms", "Hi {first}, {month} Want us to come out? Just reply with a day that works.")],
  },
};

/** The kinds built in this draft's first part. The rest show as coming soon until their part is built. */
export const AUTOMATIONS_6A: readonly AutomationKind[] = [
  "booking_confirm",
  "visit_reminder",
  "quote_followup",
  "invoice_reminder",
  "debt_reminder",
  "receipt",
];

const INTRO = /this is \{business\}/i;

/** May this text go to this kind's people? Only booking confirmations say "this is {business}". */
export function introOk(kind: AutomationKind, text: string): boolean {
  return AUTOMATIONS[kind].mayIntroduce || !INTRO.test(text);
}

export const automationPatch = z
  .object({
    on: z.boolean().optional(),
    steps: z.array(automationStep).min(1).max(8).optional(),
  })
  .strict();
export type AutomationPatch = z.infer<typeof automationPatch>;

export const automationRow = z.object({
  kind: z.enum(AUTOMATION_KINDS),
  title: z.string(),
  what: z.string(),
  purpose: z.enum(["transactional", "marketing"]),
  on: z.boolean(),
  /** False until its part of the build is done; it cannot be turned on. */
  ready: z.boolean(),
  onSince: z.string().nullable(),
  steps: z.array(automationStep),
});
export type AutomationRow = z.infer<typeof automationRow>;

export const automationDetail = automationRow.extend({
  anchor: z.string(),
  ending: z.string().nullable(),
  fills: z.array(z.string()),
  /** Each step filled in with a real client and job, so the owner sees what goes out. */
  samples: z.array(z.object({ when: z.string(), channel: z.enum(STEP_CHANNELS), text: z.string(), subject: z.string() })),
  sent: z.number(),
  /** The sample client's words ({first}, {job}, {link} ...), so the edit page fills in its preview while the owner types. */
  words: z.record(z.string(), z.string()),
  /** Shown after turning it on. */
  notice: z.string().nullable(),
});
export type AutomationDetail = z.infer<typeof automationDetail>;

/** Puts the words in: {first} and the rest. Unknown names are left empty, never shown as braces. */
export function fillAutoWords(template: string, vars: Readonly<Record<string, string>>): string {
  return template.replace(/\{([a-z]+)\}/g, (_m, k: string) => vars[k] ?? "").trim();
}

/** The full text of a step: the owner's words, then the fixed ending. */
export function stepText(kind: AutomationKind, s: AutomationStep, vars: Readonly<Record<string, string>>): string {
  const body = fillAutoWords(s.text, vars);
  const ending = AUTOMATIONS[kind].ending;
  if (s.channel !== "sms" || !ending) return body;
  const end = fillAutoWords(ending, vars);
  return end ? `${body}\n\n${end}` : body;
}

/** The email body of a step: its own email words, else the text, then the ending's link. */
export function stepEmail(kind: AutomationKind, s: AutomationStep, vars: Readonly<Record<string, string>>): string {
  const body = fillAutoWords(s.email || s.text, vars);
  const ending = AUTOMATIONS[kind].ending;
  return ending ? `${body}\n\n${fillAutoWords(ending, vars)}` : body;
}

/** When a step is said in plain words, for the edit page: "1 day before at 10:00 AM". */
export function stepWhen(kind: AutomationKind, s: AutomationStep): string {
  const n = Math.abs(s.day);
  const days = n === 0 ? "Right away" : `${String(n)} day${n === 1 ? "" : "s"} ${s.day < 0 ? "before" : "after"}`;
  const repeat = AUTOMATIONS[kind].repeatDays;
  const again = repeat ? `, then every ${String(repeat)} days` : "";
  if (!s.time) return `${days}${again}`;
  const [h, m] = s.time.split(":").map(Number) as [number, number];
  const at = `${String(h % 12 === 0 ? 12 : h % 12)}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
  return `${days} at ${at}${again}`;
}

/** How many texts a message is billed as: 160 letters in one and 153 a part after, or 70 and 67 with emoji or curly quotes. */
export function textParts(text: string): number {
  if (!text) return 0;
  const plain = !/[^\n\r\t -~]/.test(text);
  const one = plain ? 160 : 70;
  const part = plain ? 153 : 67;
  return text.length <= one ? 1 : Math.ceil(text.length / part);
}

/** The past client calendar's reason for each month (January first), filled in as {month}. */
export const MONTH_REASONS: readonly string[] = [
  "New year, fresh start! A clean home makes January easier.",
  "Valentine's Day is coming. Skip the chores and enjoy the day.",
  "Spring is almost here. Get ahead of the spring cleaning.",
  "It's spring cleaning time. We'll do the hard parts.",
  "Mother's Day is coming. A clean home is a gift she'll love.",
  "Summer is here. Spend your weekends out, not cleaning.",
  "Hosting this summer? Let us get the house ready.",
  "Back to school is coming. Start the year with a clean home.",
  "Fall is here. A good time to clean before the holidays.",
  "Holiday guests are coming soon. Book before the calendar fills up.",
  "Thanksgiving is coming. Let us get your home ready for family.",
  "The holidays are here. Give yourself a clean home this year.",
];
